import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import {
  BlockOutlined,
  CalculateOutlined,
  CloseOutlined,
  UndoOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import { LoadingPage } from '../../loading-page';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import { ISurveySession, ISurveySessionDetail } from '../../../../shared/services/survey/survey.model';
import { SurveyEmissionsBreakdownDialog } from '../../../dialogs/surveys/survey-emissions-breakdown-dialog';
import gaiaTheme from '../../../../root-components/theme';
import { SurveyHubTabProps } from '../survey-hub-page';

const headCells: IHeadCell[] = [
  { id: 'email', label: 'Email', numeric: false, sortable: false, includeInMobile: true },
  { id: 'name', label: 'Name', numeric: false, sortable: false },
  { id: 'started', label: 'Started', numeric: false, sortable: false },
  { id: 'submitted', label: 'Submitted', numeric: false, sortable: false, includeInMobile: true },
  { id: 'actions', label: '', numeric: false, sortable: false },
];

export const SurveyResponsesTab = ({ survey }: SurveyHubTabProps) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

  const surveyId = survey.id;

  const [sessions, setSessions] = useState<ISurveySession[]>();
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedSession, setSelectedSession] = useState<ISurveySessionDetail | undefined>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [provisionalSession, setProvisionalSession] = useState<{ id: string; respondentLabel: string } | null>(null);
  const [invitesSentCount, setInvitesSentCount] = useState(0);

  const loadSessions = useCallback(async () => {
    setSessions(undefined);
    const [sessionsRes, invitationsRes] = await Promise.allSettled([
      surveyAdminService.getSessions(surveyId),
      surveyAdminService.getInvitations(surveyId, { page: 0, perPage: 1, sentOnly: true }),
    ]);
    setSessions(sessionsRes.status === 'fulfilled' ? sessionsRes.value.rows : []);
    setInvitesSentCount(invitationsRes.status === 'fulfilled' ? Number(invitationsRes.value.count) : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const runInvalidate = async (sessionId: string) => {
    try {
      await surveyAdminService.invalidateSurveySession(sessionId);
      userNotificationsContext.openSnackbar({ open: true, message: 'Response invalidated', backgroundColor: 'primary.dark' });
      loadSessions();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not invalidate this response', backgroundColor: 'text.primary' });
    }
  };

  const runReinstate = async (sessionId: string) => {
    try {
      await surveyAdminService.reinstateSurveySession(sessionId);
      userNotificationsContext.openSnackbar({ open: true, message: 'Invalidation removed', backgroundColor: 'primary.dark' });
      loadSessions();
      setSelectedSession(cur => (cur?.id === sessionId ? { ...cur, invalidated: false } : cur));
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not reinstate this response', backgroundColor: 'text.primary' });
    }
  };

  const runDeleteInvalidated = async (sessionId: string) => {
    try {
      await surveyAdminService.deleteSurveySession(sessionId);
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'Session, invitation, and answers removed',
        backgroundColor: 'primary.dark',
      });
      loadSessions();
      if (detailOpen && selectedSession?.id === sessionId) {
        setDetailOpen(false);
        setSelectedSession(undefined);
      }
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not delete this response', backgroundColor: 'text.primary' });
    }
  };

  const confirmInvalidate = (sessionId: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Invalidate response?',
      message:
        'Invalidated responses are excluded from provisional and combined provisional emissions and cannot be committed. You can remove the invalidation later, or permanently delete this response.',
      buttonCopy: 'Invalidate',
      cancelButtonCopy: 'Cancel',
    })
      .then((ok) => {
        if (ok) runInvalidate(sessionId);
      })
      .catch(() => undefined);
  };

  const confirmDeleteInvalidated = (sessionId: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete response?',
      message:
        'This removes the respondent session, the linked invitation (if any), and all saved answers. This cannot be undone.',
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    })
      .then((ok) => {
        if (ok) runDeleteInvalidated(sessionId);
      })
      .catch(() => undefined);
  };

  const confirmReinstate = (sessionId: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Remove invalidation?',
      message:
        'This response will again count toward provisional emissions and may be eligible to commit.',
      buttonCopy: 'Reinstate',
      cancelButtonCopy: 'Cancel',
    })
      .then((ok) => {
        if (ok) runReinstate(sessionId);
      })
      .catch(() => undefined);
  };

  const handleViewSession = async (sessionId: string) => {
    try {
      const detail = await surveyAdminService.getSessionDetail(sessionId);
      setSelectedSession(detail);
      setDetailOpen(true);
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to load session detail', backgroundColor: 'text.primary' });
    }
  };

  const isAnonymous = survey.anonymous ?? false;

  const rows = (sessions ?? []).map(session => ({
    id: session.id,
    email: isAnonymous ? 'Anonymous' : (session.respondentEmail ?? '—'),
    name: isAnonymous ? '—' : (session.respondentName ?? '—'),
    started: moment(session.startedAt).format('DD/MM/YYYY HH:mm'),
    submitted: (
      <Stack direction='row' spacing={0.5} alignItems='center' flexWrap='wrap'>
        {session.invalidated && (
          <Chip label='Invalidated' color='warning' size='small' sx={{ mr: 0.5 }} />
        )}
        {session.submittedAt ? (
          <Chip label={moment(session.submittedAt).format('DD/MM/YYYY HH:mm')} color='success' size='small' />
        ) : (
          <Chip label='In progress' color='warning' size='small' />
        )}
        {session.submittedAt && session.committedToReportPeriod && (
          <Chip label='Committed' size='small' variant='outlined' />
        )}
      </Stack>
    ),
    actions: (
      <Stack direction='row' spacing={0.25} alignItems='center'>
        <Tooltip title='View response'>
          <IconButton size='small' onClick={() => handleViewSession(session.id)}>
            <VisibilityOutlined fontSize='small' />
          </IconButton>
        </Tooltip>
        {survey.purpose !== 'SUPPLIER_PRODUCT_DATA' && (
          <Tooltip title={session.invalidated ? 'Excluded from provisional emissions' : 'Show provisional emissions'}>
            <span>
              <IconButton
                size='small'
                disabled={Boolean(session.invalidated)}
                onClick={() => setProvisionalSession({
                  id: session.id,
                  respondentLabel: isAnonymous ? 'Anonymous' : (session.respondentEmail ?? ''),
                })}
              >
                <CalculateOutlined fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {!session.committedToReportPeriod && !session.invalidated && (
          <Tooltip title='Invalidate response'>
            <IconButton size='small' onClick={() => confirmInvalidate(session.id)}>
              <BlockOutlined fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        {!session.committedToReportPeriod && session.invalidated && (
          <>
            <Tooltip title='Remove invalidation'>
              <IconButton size='small' onClick={() => confirmReinstate(session.id)}>
                <UndoOutlined fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete session, invitation and answers'>
              <IconButton size='small' onClick={() => confirmDeleteInvalidated(session.id)}>
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    ),
  }));

  const started = sessions?.length ?? 0;
  const submitted = sessions?.filter(s => s.submittedAt).length ?? 0;

  return (
    <Box>
      <Stack direction={isMobileMode ? 'column' : 'row'} spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
          <Typography variant='h4' fontWeight={700}>{invitesSentCount}</Typography>
          <Typography color='text.secondary'>Invites sent</Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
          <Typography variant='h4' fontWeight={700}>{started}</Typography>
          <Typography color='text.secondary'>Started</Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
          <Typography variant='h4' fontWeight={700} color='success.main'>{submitted}</Typography>
          <Typography color='text.secondary'>Submitted</Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
          <Typography variant='h4' fontWeight={700} color='warning.main'>{started - submitted}</Typography>
          <Typography color='text.secondary'>In progress</Typography>
        </Box>
      </Stack>

      <LoadingPage pageReady={!!sessions}>
        <TableDisplay
          rows={rows}
          headCells={headCells}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          totalRecords={sessions?.length ?? 0}
        />
      </LoadingPage>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth='md' fullWidth>
        <Box sx={{ p: 3 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
            <DialogTitle sx={{ p: 0 }}>
              Response: {isAnonymous ? 'Anonymous' : (selectedSession?.respondentEmail ?? '—')}
            </DialogTitle>
            <IconButton onClick={() => setDetailOpen(false)}>
              <CloseOutlined />
            </IconButton>
          </Stack>
          <DialogContent sx={{ p: 0 }}>
            {(selectedSession?.invalidated ?? false) && (
              <Chip
                label='Invalidated: excluded from provisional emissions and cannot be committed'
                color='warning'
                size='small'
                sx={{ mb: 2 }}
              />
            )}
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Submitted: {selectedSession?.submittedAt ? moment(selectedSession.submittedAt).format('DD/MM/YYYY HH:mm') : 'Not yet submitted'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {(selectedSession?.responseItems ?? []).map((item, index) => {
              const prompt = item.surveyQuestion?.prompt ?? `Question ${index + 1}`;
              const hasAnswer =
                item.textValue != null ||
                (item.moreDetailsText != null && String(item.moreDetailsText).trim() !== '') ||
                item.numericValue != null ||
                item.booleanValue != null ||
                (item.selectedOptionLabels && item.selectedOptionLabels.length > 0) ||
                (item.optionIds && item.optionIds.length > 0);

              return (
                <Box key={item.surveyQuestionId ?? index} sx={{ mb: 2 }}>
                  <Typography variant='subtitle2'>{prompt}</Typography>
                  {item.surveyQuestion?.helpText && (
                    <Typography variant='caption' color='text.secondary'>{item.surveyQuestion.helpText}</Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    {item.textValue != null && (
                      <Typography variant='body2'>{item.textValue}</Typography>
                    )}
                    {item.numericValue != null && (
                      <Typography variant='body2'>{item.numericValue}</Typography>
                    )}
                    {item.booleanValue != null && (
                      <Typography variant='body2'>{item.booleanValue ? 'Yes' : 'No'}</Typography>
                    )}
                    {item.selectedOptionLabels && item.selectedOptionLabels.length > 0 && (
                      <Stack direction='row' spacing={1} flexWrap='wrap'>
                        {item.selectedOptionLabels.map(label => (
                          <Chip key={label} label={label} size='small' color='primary' variant='outlined' />
                        ))}
                      </Stack>
                    )}
                    {item.moreDetailsText != null && String(item.moreDetailsText).trim() !== '' && (
                      <Typography variant='body2' sx={{ mt: item.textValue != null || item.numericValue != null || (item.selectedOptionLabels && item.selectedOptionLabels.length) ? 1 : 0 }}>
                        <Typography component='span' variant='caption' color='text.secondary' display='block'>
                          Additional details
                        </Typography>
                        {item.moreDetailsText}
                      </Typography>
                    )}
                    {!hasAnswer && (
                      <Typography color='text.secondary' variant='body2' fontStyle='italic'>No answer recorded</Typography>
                    )}
                  </Box>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              );
            })}
            {(selectedSession?.responseItems ?? []).length === 0 && (
              <Typography color='text.secondary'>No responses recorded.</Typography>
            )}
          </DialogContent>
        </Box>
      </Dialog>

      <SurveyEmissionsBreakdownDialog
        mode='session'
        open={provisionalSession != null}
        sessionId={provisionalSession?.id ?? null}
        respondentLabel={provisionalSession?.respondentLabel}
        onClose={() => setProvisionalSession(null)}
      />
    </Box>
  );
};
